/** Normalize Q88 / vessel master API fields into cargo heating onboarding keys. */

export interface TankerTypeOption {
  label: string;
  value: string;
}

export interface DropdownOption {
  label: string;
  value: string;
}

function pickField(row: Record<string, unknown>, ...keys: string[]): unknown {
  for (const key of keys) {
    const value = row[key];
    if (value != null && value !== '') {
      return value;
    }
  }
  return null;
}

function normalizeToken(value: unknown): string {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

/** Segment: vessel_segment → vesselSegment → segment (exact value preserved). */
export function resolveSegmentFromVessel(row: Record<string, unknown>): string {
  const raw = pickField(row, 'vessel_segment', 'vesselSegment', 'segment');
  return raw != null ? String(raw).trim() : '';
}

/**
 * Map API vessel type text to canonical tanker type label.
 * Combined carriers (e.g. Crude Oil/Product Carrier) must resolve to Product before Crude.
 */
export function resolveTankerType(value: string | null | undefined): string | null {
  const normalized = normalizeToken(value);
  if (!normalized) {
    return null;
  }

  if (
    normalized.includes('crude oil/product carrier') ||
    normalized.includes('chemical/product') ||
    normalized === 'product' ||
    normalized.includes('product carrier')
  ) {
    return 'Product';
  }

  if (normalized.includes('crude oil tanker') || normalized === 'crude') {
    return 'Crude';
  }

  if (normalized.includes('chemical')) {
    return 'Chemical';
  }

  if (normalized.includes('asphalt') || normalized.includes('bitumen')) {
    return 'Asphalt';
  }

  return null;
}

function matchDropdownOptionValue(
  resolved: string,
  options: DropdownOption[],
): string {
  const token = normalizeToken(resolved).replace(/\s+/g, '');
  if (!token) {
    return '';
  }

  const exact = options.find((option) => {
    const value = normalizeToken(option.value).replace(/\s+/g, '');
    const label = normalizeToken(option.label).replace(/\s+/g, '');
    return value === token || label === token;
  });
  if (exact) {
    return exact.value;
  }

  if (token === 'crude') {
    const crudeOil = options.find(
      (option) => normalizeToken(option.value) === 'crudeoil',
    );
    if (crudeOil) {
      return crudeOil.value;
    }
  }

  if (token === 'product') {
    const product = options.find((option) => option.value === 'Product');
    if (product) {
      return product.value;
    }
  }

  return resolved.trim();
}

export function matchSegmentToOptions(
  segment: string,
  options: DropdownOption[] | string[],
): string {
  const raw = String(segment ?? '').trim();
  if (!raw) {
    return '';
  }

  if (!options.length) {
    return raw;
  }

  if (typeof options[0] === 'string') {
    const list = options as string[];
    const token = raw.toLowerCase();
    const match = list.find((entry) => entry.toLowerCase() === token);
    return match ?? raw;
  }

  return matchDropdownOptionValue(raw, options as DropdownOption[]);
}

export function resolveTankerTypeFromVessel(
  row: Record<string, unknown>,
  options: TankerTypeOption[] = [],
): string {
  const explicitTankerType = pickField(row, 'tanker_type', 'tankerType');
  const vesselType = pickField(
    row,
    'type_of_vessel',
    'typeOfVessel',
    'type_of_ship',
    'typeOfShip',
    'ship_type',
    'shipType',
    'vessel_type',
    'vesselType',
  );

  const resolved =
    (explicitTankerType != null
      ? resolveTankerType(String(explicitTankerType)) ??
        String(explicitTankerType).trim()
      : '') ||
  resolveTankerType(vesselType != null ? String(vesselType) : '') ||
    '';

  if (!resolved) {
    return '';
  }

  if (!options.length) {
    return resolved === 'Crude' ? 'Crude Oil' : resolved;
  }

  return matchDropdownOptionValue(resolved, options);
}

export function normalizeVesselMasterRow(
  raw: Record<string, unknown> | null | undefined,
): Record<string, unknown> {
  if (!raw || typeof raw !== 'object') {
    return {};
  }

  const cargoTanks = toNumber(
    pickField(raw, 'number_of_cargo_tanks', 'numberOfCargoTanks'),
  );
  const slopTanks = toNumber(
    pickField(raw, 'number_of_slop_tanks', 'numberOfSlopTanks'),
  );
  let inferredTankCount: number | null = null;
  if (cargoTanks > 0) {
    inferredTankCount = cargoTanks + (slopTanks > 0 ? slopTanks * 2 : 0);
  }

  const rawCargoTankCount = pickField(
    raw,
    'number_of_cargo_tanks',
    'numberOfCargoTanks',
  );

  return {
    ...raw,
    vessel_name: pickField(raw, 'vessel_name', 'vesselName'),
    imo_number: pickField(raw, 'imo_number', 'imo', 'vessel_imo'),
    vessel_segment: pickField(raw, 'vessel_segment', 'vesselSegment', 'segment'),
    segment: pickField(raw, 'vessel_segment', 'vesselSegment', 'segment'),
    type_of_ship: pickField(raw, 'type_of_ship', 'typeOfShip', 'ship_type', 'shipType'),
    tanker_type: pickField(raw, 'tanker_type', 'tankerType'),
    type_of_vessel: pickField(
      raw,
      'type_of_vessel',
      'typeOfVessel',
      'vessel_type',
      'vesselType',
    ),
    number_of_cargo_tanks: rawCargoTankCount,
    boiler_make: pickField(raw, 'boiler_make_type', 'boiler_make', 'boilerMake'),
    boiler_capacity: pickField(
      raw,
      'boiler_capacity_metric_tonnes_per_hour',
      'boiler_capacity',
      'boilerCapacity',
    ),
    number_of_boilers: pickField(
      raw,
      'boiler_number',
      'number_of_boilers',
      'numberOfBoilers',
    ),
    egcs_scrubber: pickField(
      raw,
      'exhaust_gas_cleaning_system',
      'egcs_scrubber',
      'egcsScrubber',
      'vessel_fitted_with_egcs',
    ),
    cargo_tanks_heating_system_type: pickField(
      raw,
      'cargo_tanks_heating_system_type',
    ),
    cargo_tanks_heating_system_coiled: pickField(
      raw,
      'cargo_tanks_heating_system_coiled',
    ),
    thermal_oil_heating_system_fitted: pickField(
      raw,
      'thermal_oil_heating_system_fitted',
    ),
    inferred_tank_count: inferredTankCount,
  };
}

export function mergeVesselMasterRows(
  q88: Record<string, unknown> | null | undefined,
  saved: Record<string, unknown> | null | undefined,
): Record<string, unknown> {
  const normalizedQ88 = normalizeVesselMasterRow(q88);
  if (!saved || typeof saved !== 'object') {
    return normalizedQ88;
  }
  return { ...saved, ...normalizedQ88 };
}

export function mapHeatingArrangementFromVessel(
  row: Record<string, unknown>,
): string | null {
  const thermalOil = String(row['thermal_oil_heating_system_fitted'] ?? '')
    .trim()
    .toLowerCase();
  if (thermalOil === 'yes') {
    return 'Heat Exchanger';
  }

  const heatingType = String(row['cargo_tanks_heating_system_type'] ?? '')
    .trim()
    .toLowerCase();
  if (!heatingType) {
    const coiled = String(row['cargo_tanks_heating_system_coiled'] ?? '')
      .trim()
      .toLowerCase();
    if (coiled === 'yes') {
      return 'Steam Coils';
    }
    return null;
  }

  if (/heat\s*exchanger|thermal\s*oil|hot\s*oil/i.test(heatingType)) {
    return 'Heat Exchanger';
  }
  if (/steam|coil|grid\s*pipe/i.test(heatingType)) {
    return 'Steam Coils';
  }

  return null;
}

function toNumber(value: unknown): number {
  if (value == null || value === '') {
    return 0;
  }
  const parsed = Number(String(value).replace(/[^\d.-]/g, ''));
  return Number.isFinite(parsed) ? parsed : 0;
}
